/**
 * markdown.pipe.ts
 * ----------------
 * Converts a subset of Markdown to safe HTML for display in the chat panel.
 *
 * Supported syntax:
 *   ### Heading 3
 *   ## Heading 2
 *   **bold**
 *   - bullet item
 *   blank lines → paragraph breaks
 */

import { Pipe, PipeTransform }        from '@angular/core';
import { DomSanitizer, SafeHtml }     from '@angular/platform-browser';

@Pipe({ name: 'markdown', standalone: true })
export class MarkdownPipe implements PipeTransform {

  constructor(private readonly sanitizer: DomSanitizer) {}

  transform(value: string | null | undefined): SafeHtml {
    if (!value) return '';
    const html = this._toHtml(value);
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private _toHtml(md: string): string {
    const lines = md.split('\n');
    const output: string[] = [];
    let inList = false;

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];

      // Close open list when we hit a non-bullet line
      const isBullet = /^[-*•]\s+/.test(line.trimStart());
      if (inList && !isBullet) {
        output.push('</ul>');
        inList = false;
      }

      // ### Heading 3
      if (/^###\s+/.test(line)) {
        output.push(`<h4 class="md-h4">${this._inline(line.replace(/^###\s+/, ''))}</h4>`);
        continue;
      }

      // ## Heading 2
      if (/^##\s+/.test(line)) {
        output.push(`<h3 class="md-h3">${this._inline(line.replace(/^##\s+/, ''))}</h3>`);
        continue;
      }

      // # Heading 1
      if (/^#\s+/.test(line)) {
        output.push(`<h3 class="md-h3">${this._inline(line.replace(/^#\s+/, ''))}</h3>`);
        continue;
      }

      // Bullet list item
      if (isBullet) {
        if (!inList) { output.push('<ul class="md-list">'); inList = true; }
        output.push(`<li>${this._inline(line.replace(/^[-*•]\s+/, ''))}</li>`);
        continue;
      }

      // Blank line
      if (line.trim() === '') {
        output.push('<br>');
        continue;
      }

      // Regular paragraph line
      output.push(`<p class="md-p">${this._inline(line)}</p>`);
    }

    if (inList) output.push('</ul>');
    return output.join('');
  }

  /** Process inline formatting: **bold** and *italic* */
  private _inline(text: string): string {
    return text
      // Escape raw HTML to prevent injection
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // **bold**
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // *italic*
      .replace(/\*(.+?)\*/g, '<em>$1</em>');
  }
}
